import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from '../hooks/useTranslation';
import { useCurrency } from '../hooks/useCurrency';

interface Flight {
    id: string;
    airline: string;
    flightNumber: string;
    departure: {
        city: string;
        airport: string;
        time: string;
        date: string;
    };
    arrival: {
        city: string;
        airport: string;
        time: string;
        date: string;
    };
    duration: string;
    price: number;
    stops: number;
    cabinClass: string;
    airlineLogo: string;
}

const Flights: React.FC = () => {
    const { t } = useTranslation();
    const { formatPrice } = useCurrency();
    const [flights, setFlights] = useState<Flight[]>([]);
    const [filteredFlights, setFilteredFlights] = useState<Flight[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    
    // Search filters
    const [fromCity, setFromCity] = useState('');
    const [toCity, setToCity] = useState('');
    const [departureDate, setDepartureDate] = useState('');
    const [returnDate, setReturnDate] = useState('');
    const [passengers, setPassengers] = useState(1);
    const [cabinClass, setCabinClass] = useState('economy');
    
    // Additional filters
    const [priceRange, setPriceRange] = useState([0, 2000]);
    const [maxStops, setMaxStops] = useState(2);
    const [airlines, setAirlines] = useState<string[]>([]);
    const [sortBy, setSortBy] = useState('price');

    // Sample cities
    const cities = ['Istanbul', 'Dubai', 'London', 'Paris', 'New York', 'Tokyo', 'Singapore', 'Bangkok', 'Cairo', 'Riyadh'];
    const cabinClasses = [
        { value: 'economy', label: 'Economy', icon: '🛋️' },
        { value: 'premium', label: 'Premium Economy', icon: '🛋️✨' },
        { value: 'business', label: 'Business', icon: '💼' },
        { value: 'first', label: 'First Class', icon: '👑' }
    ];

    // Mock flights data
    const mockFlights: Flight[] = [
        {
            id: '1',
            airline: 'Turkish Airlines',
            flightNumber: 'TK123',
            departure: { city: 'Istanbul', airport: 'IST', time: '08:00', date: '2024-01-15' },
            arrival: { city: 'Dubai', airport: 'DXB', time: '12:30', date: '2024-01-15' },
            duration: '4h 30m',
            price: 450,
            stops: 0,
            cabinClass: 'economy',
            airlineLogo: '🇹🇷'
        },
        {
            id: '2',
            airline: 'Emirates',
            flightNumber: 'EK456',
            departure: { city: 'Dubai', airport: 'DXB', time: '14:00', date: '2024-01-15' },
            arrival: { city: 'London', airport: 'LHR', time: '18:30', date: '2024-01-15' },
            duration: '7h 30m',
            price: 680,
            stops: 0,
            cabinClass: 'economy',
            airlineLogo: '🇦🇪'
        },
        {
            id: '3',
            airline: 'British Airways',
            flightNumber: 'BA789',
            departure: { city: 'London', airport: 'LHR', time: '10:30', date: '2024-01-15' },
            arrival: { city: 'Paris', airport: 'CDG', time: '13:00', date: '2024-01-15' },
            duration: '1h 30m',
            price: 120,
            stops: 0,
            cabinClass: 'economy',
            airlineLogo: '🇬🇧'
        },
        {
            id: '4',
            airline: 'Air France',
            flightNumber: 'AF234',
            departure: { city: 'Paris', airport: 'CDG', time: '16:00', date: '2024-01-15' },
            arrival: { city: 'New York', airport: 'JFK', time: '19:00', date: '2024-01-15' },
            duration: '8h 00m',
            price: 890,
            stops: 0,
            cabinClass: 'economy',
            airlineLogo: '🇫🇷'
        },
        {
            id: '5',
            airline: 'Qatar Airways',
            flightNumber: 'QR567',
            departure: { city: 'Doha', airport: 'DOH', time: '02:00', date: '2024-01-15' },
            arrival: { city: 'Tokyo', airport: 'NRT', time: '16:00', date: '2024-01-15' },
            duration: '9h 00m',
            price: 750,
            stops: 1,
            cabinClass: 'economy',
            airlineLogo: '🇶🇦'
        }
    ];

    useEffect(() => {
        // Simulate loading
        setTimeout(() => {
            setFlights(mockFlights);
            setFilteredFlights(mockFlights);
            setIsLoading(false);
        }, 1000);
    }, []);

    useEffect(() => {
        // Apply filters
        let filtered = flights;

        // Route filter
        if (fromCity) {
            filtered = filtered.filter(flight => 
                flight.departure.city.toLowerCase().includes(fromCity.toLowerCase())
            );
        }
        if (toCity) {
            filtered = filtered.filter(flight => 
                flight.arrival.city.toLowerCase().includes(toCity.toLowerCase())
            );
        }

        // Price filter
        filtered = filtered.filter(flight => 
            flight.price >= priceRange[0] && flight.price <= priceRange[1]
        );

        // Stops filter
        filtered = filtered.filter(flight => flight.stops <= maxStops);

        // Airlines filter
        if (airlines.length > 0) {
            filtered = filtered.filter(flight => 
                airlines.includes(flight.airline)
            );
        }

        // Sort
        filtered.sort((a, b) => {
            switch (sortBy) {
                case 'price':
                    return a.price - b.price;
                case 'duration':
                    return parseInt(a.duration) - parseInt(b.duration);
                case 'departure':
                    return a.departure.time.localeCompare(b.departure.time);
                default:
                    return a.price - b.price;
            }
        });

        setFilteredFlights(filtered);
    }, [flights, fromCity, toCity, priceRange, maxStops, airlines, sortBy]);

    const clearFilters = () => {
        setFromCity('');
        setToCity('');
        setDepartureDate('');
        setReturnDate('');
        setPassengers(1);
        setCabinClass('economy');
        setPriceRange([0, 2000]);
        setMaxStops(2);
        setAirlines([]);
        setSortBy('price');
    };

    const handleSearch = () => {
        // In a real app, this would trigger an API call
        console.log('Searching flights...', { fromCity, toCity, departureDate, returnDate, passengers, cabinClass });
    };

    if (isLoading) {
        return (
            <div className="flights-page">
                <div className="container">
                    <div className="loading-container">
                        <div className="loading-spinner"></div>
                        <p>{t('common.loading')}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flights-page">
            <div className="container">
                {/* Hero Section */}
                <motion.section 
                    className="flights-hero"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    <h1>{t('flights.title', 'Flights')}</h1>
                    <p>{t('flights.subtitle', 'Find the best flights to your destination')}</p>
                </motion.section>

                {/* Search Form */}
                <motion.section 
                    className="flights-search"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                >
                    <div className="search-form">
                        <div className="search-row">
                            <div className="search-field">
                                <label>{t('flights.from', 'From')}</label>
                                <input
                                    type="text"
                                    placeholder={t('flights.fromPlaceholder', 'City or Airport')}
                                    value={fromCity}
                                    onChange={(e) => setFromCity(e.target.value)}
                                    className="search-input"
                                />
                            </div>
                            <div className="search-field">
                                <label>{t('flights.to', 'To')}</label>
                                <input
                                    type="text"
                                    placeholder={t('flights.toPlaceholder', 'City or Airport')}
                                    value={toCity}
                                    onChange={(e) => setToCity(e.target.value)}
                                    className="search-input"
                                />
                            </div>
                        </div>
                        
                        <div className="search-row">
                            <div className="search-field">
                                <label>{t('flights.departure', 'Departure')}</label>
                                <input
                                    type="date"
                                    value={departureDate}
                                    onChange={(e) => setDepartureDate(e.target.value)}
                                    className="search-input"
                                />
                            </div>
                            <div className="search-field">
                                <label>{t('flights.return', 'Return (Optional)')}</label>
                                <input
                                    type="date"
                                    value={returnDate}
                                    onChange={(e) => setReturnDate(e.target.value)}
                                    className="search-input"
                                />
                            </div>
                        </div>

                        <div className="search-row">
                            <div className="search-field">
                                <label>{t('flights.passengers', 'Passengers')}</label>
                                <select
                                    value={passengers}
                                    onChange={(e) => setPassengers(parseInt(e.target.value))}
                                    className="search-select"
                                >
                                    {[1, 2, 3, 4, 5, 6, 7, 8].map(num => (
                                        <option key={num} value={num}>{num}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="search-field">
                                <label>{t('flights.cabinClass', 'Cabin Class')}</label>
                                <select
                                    value={cabinClass}
                                    onChange={(e) => setCabinClass(e.target.value)}
                                    className="search-select"
                                >
                                    {cabinClasses.map(cls => (
                                        <option key={cls.value} value={cls.value}>
                                            {cls.icon} {cls.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <button onClick={handleSearch} className="search-button">
                            {t('flights.search', 'Search Flights')}
                        </button>
                    </div>
                </motion.section>

                {/* Filters */}
                <motion.section 
                    className="flights-filters"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                >
                    <div className="filters-header">
                        <h3>{t('flights.filters', 'Filters')}</h3>
                        <button onClick={clearFilters} className="clear-filters-btn">
                            {t('flights.clearFilters', 'Clear All')}
                        </button>
                    </div>

                    <div className="filters-grid">
                        <div className="filter-group">
                            <label>{t('flights.priceRange', 'Price Range')}</label>
                            <div className="price-range">
                                <input
                                    type="number"
                                    placeholder="Min"
                                    value={priceRange[0]}
                                    onChange={(e) => setPriceRange([parseInt(e.target.value) || 0, priceRange[1]])}
                                    className="filter-input"
                                />
                                <span>-</span>
                                <input
                                    type="number"
                                    placeholder="Max"
                                    value={priceRange[1]}
                                    onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value) || 2000])}
                                    className="filter-input"
                                />
                            </div>
                        </div>

                        <div className="filter-group">
                            <label>{t('flights.maxStops', 'Max Stops')}</label>
                            <select
                                value={maxStops}
                                onChange={(e) => setMaxStops(parseInt(e.target.value))}
                                className="filter-select"
                            >
                                <option value={0}>{t('flights.direct', 'Direct')}</option>
                                <option value={1}>{t('flights.oneStop', '1 Stop')}</option>
                                <option value={2}>{t('flights.twoStops', '2 Stops')}</option>
                            </select>
                        </div>

                        <div className="filter-group">
                            <label>{t('flights.sortBy', 'Sort By')}</label>
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="filter-select"
                            >
                                <option value="price">{t('flights.sortPrice', 'Price')}</option>
                                <option value="duration">{t('flights.sortDuration', 'Duration')}</option>
                                <option value="departure">{t('flights.sortDeparture', 'Departure Time')}</option>
                            </select>
                        </div>
                    </div>
                </motion.section>

                {/* Results */}
                <motion.section 
                    className="flights-results"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.6 }}
                >
                    <div className="results-header">
                        <h3>
                            {t('flights.results', 'Results')} ({filteredFlights.length})
                        </h3>
                        <p>
                            {filteredFlights.length > 0 
                                ? t('flights.flightsFound', 'flights found')
                                : t('flights.noFlights', 'No flights found matching your criteria')
                            }
                        </p>
                    </div>

                    {filteredFlights.length === 0 ? (
                        <div className="no-results">
                            <div className="no-results-icon">✈️</div>
                            <h4>{t('flights.noResultsTitle', 'No Flights Found')}</h4>
                            <p>{t('flights.noResultsDesc', 'Try adjusting your search criteria')}</p>
                            <button onClick={clearFilters} className="btn btn-primary">
                                {t('flights.clearFilters', 'Clear Filters')}
                            </button>
                        </div>
                    ) : (
                        <div className="flights-list">
                            {filteredFlights.map((flight, index) => (
                                <motion.div
                                    key={flight.id}
                                    className="flight-card"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4, delay: index * 0.1 }}
                                    whileHover={{ y: -2, scale: 1.01 }}
                                >
                                    <div className="flight-header">
                                        <div className="airline-info">
                                            <span className="airline-logo">{flight.airlineLogo}</span>
                                            <div>
                                                <h4 className="airline-name">{flight.airline}</h4>
                                                <span className="flight-number">{flight.flightNumber}</span>
                                            </div>
                                        </div>
                                        <div className="flight-price">
                                            <span className="price-amount">{formatPrice(flight.price)}</span>
                                            <span className="price-per">per passenger</span>
                                        </div>
                                    </div>

                                    <div className="flight-route">
                                        <div className="route-segment departure">
                                            <div className="time">{flight.departure.time}</div>
                                            <div className="city">{flight.departure.city}</div>
                                            <div className="airport">{flight.departure.airport}</div>
                                        </div>
                                        
                                        <div className="route-connection">
                                            <div className="duration">{flight.duration}</div>
                                            <div className="connection-line">
                                                <span className="line"></span>
                                                <span className="plane">✈️</span>
                                            </div>
                                            <div className="stops">
                                                {flight.stops === 0 ? t('flights.direct', 'Direct') : 
                                                 flight.stops === 1 ? t('flights.oneStop', '1 Stop') : 
                                                 t('flights.twoStops', '2 Stops')}
                                            </div>
                                        </div>

                                        <div className="route-segment arrival">
                                            <div className="time">{flight.arrival.time}</div>
                                            <div className="city">{flight.arrival.city}</div>
                                            <div className="airport">{flight.arrival.airport}</div>
                                        </div>
                                    </div>

                                    <div className="flight-footer">
                                        <div className="flight-details">
                                            <span className="cabin-class">{cabinClasses.find(c => c.value === flight.cabinClass)?.icon} {flight.cabinClass}</span>
                                            <span className="stops-info">
                                                {flight.stops === 0 ? t('flights.direct', 'Direct') : 
                                                 `${flight.stops} ${t('flights.stops', 'stops')}`}
                                            </span>
                                        </div>
                                        <button className="btn btn-primary book-flight-btn">
                                            {t('flights.bookNow', 'Book Now')}
                                        </button>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    )}
                </motion.section>
            </div>
        </div>
    );
};

export default Flights;

